import { z } from "zod";
import { FailureSchema, IsoDateTimeSchema } from "./common";
import { BookIdSchema, ChapterIdSchema, NodeIdSchema } from "./ids";
import { JobSummarySchema } from "./jobs";
import { BookLanguageSchema } from "./states";

/** What Parse counted; shown next to the Book facts on the confirmation screen. */
export const ParseReportSchema = z.strictObject({
  words: z.int().nonnegative(),
  characters: z.int().nonnegative(),
  nodes: z.int().nonnegative(),
  headings: z.int().nonnegative(),
  paragraphs: z.int().nonnegative(),
  listItems: z.int().nonnegative(),
  footnotes: z.int().nonnegative(),
  tables: z.int().nonnegative(),
  figures: z.int().nonnegative(),
  pageBreaks: z.int().nonnegative(),
});
export type ParseReport = z.infer<typeof ParseReportSchema>;

/** One proposed Chapter: a node range [nodeStart, nodeEnd) over ordinals, so merge and split are integer edits. */
export const ProposalChapterSchema = z.strictObject({
  id: ChapterIdSchema,
  position: z.int().positive(),
  /** Display number among included Chapters; null when excluded. */
  number: z.int().positive().nullable(),
  title: z.string(),
  /** The Author's current choice (draft) — starts as proposalExcluded. */
  excluded: z.boolean(),
  /** What the Chapter Proposal said, kept so the screen can show "proposed: excluded". */
  proposalExcluded: z.boolean(),
  /** The proposal's reason for an exclusion, in the Book's language; null when none. */
  reason: z.string().nullable(),
  nodeStart: z.int().nonnegative(),
  nodeEnd: z.int().positive(),
  startNodeId: NodeIdSchema,
  wordCount: z.int().nonnegative(),
  characterCount: z.int().nonnegative(),
  firstLine: z.string().nullable(),
});
export type ProposalChapter = z.infer<typeof ProposalChapterSchema>;

const proposalBase = {
  bookId: BookIdSchema,
  parseReport: ParseReportSchema,
  nodeCount: z.int().nonnegative(),
};

/** The proposal job has not finished; the confirmation screen waits on it. */
export const ChapterProposalPendingSchema = z.strictObject({
  status: z.literal("pending"),
  ...proposalBase,
  job: JobSummarySchema.nullable(),
});

export const ChapterProposalFailedSchema = z.strictObject({
  status: z.literal("failed"),
  ...proposalBase,
  failure: FailureSchema,
  /** The job POST /jobs/:id/retry targets. */
  job: JobSummarySchema.nullable(),
});

export const ChapterProposalReadySchema = z.strictObject({
  status: z.literal("ready"),
  ...proposalBase,
  chapters: z.array(ProposalChapterSchema).min(1),
  /** The Author's current Calibration Chapter choice (draft), defaulting to the suggestion. */
  calibrationChapterId: ChapterIdSchema.nullable(),
  /** The proposal's suggestion: the first narrative Chapter, else the first included one. */
  suggestedCalibrationChapterId: ChapterIdSchema.nullable(),
  notes: z.string().nullable(),
  proposedAt: IsoDateTimeSchema,
  /** False once the Book is confirmed: the list is then history, not a draft. */
  editable: z.boolean(),
});
export type ChapterProposalReady = z.infer<typeof ChapterProposalReadySchema>;

export const ChapterProposalSchema = z.discriminatedUnion("status", [
  ChapterProposalPendingSchema,
  ChapterProposalFailedSchema,
  ChapterProposalReadySchema,
]);
export type ChapterProposal = z.infer<typeof ChapterProposalSchema>;

export const ProposalDraftChapterSchema = z.strictObject({
  title: z.string().trim().min(1).max(300),
  /** Ordinal of the first node; the Chapter runs to the next Chapter's start, the last one to the end. */
  nodeStart: z.int().nonnegative(),
  excluded: z.boolean(),
});
export type ProposalDraftChapter = z.infer<typeof ProposalDraftChapterSchema>;

const draftShape = {
  /** The full list, replacing the stored draft. Contiguous by construction; the first must start at 0. */
  chapters: z.array(ProposalDraftChapterSchema).min(1),
  /** Index into `chapters` of the Calibration Chapter; must point at an included Chapter. */
  calibrationIndex: z.int().nonnegative(),
};

function draftIsValid(draft: {
  chapters: readonly ProposalDraftChapter[];
  calibrationIndex: number;
}): boolean {
  if (draft.chapters[0]?.nodeStart !== 0) return false;
  for (let i = 1; i < draft.chapters.length; i++) {
    const prev = draft.chapters[i - 1];
    const cur = draft.chapters[i];
    if (!prev || !cur || cur.nodeStart <= prev.nodeStart) return false;
  }
  const calibration = draft.chapters[draft.calibrationIndex];
  return calibration !== undefined && !calibration.excluded;
}

const DRAFT_MESSAGE =
  "chapters must start at node 0, increase strictly, and calibrationIndex must point at an included chapter";

/**
 * Whether every Chapter of a draft starts at a node the Manuscript actually
 * has. The schema cannot check this — it never sees the Book — so both
 * transports call it with the Book's node count after the body parses and
 * answer VALIDATION_FAILED when it fails.
 */
export function draftFitsManuscript(
  draft: { readonly chapters: readonly ProposalDraftChapter[] },
  nodeCount: number,
): boolean {
  return draft.chapters.every((chapter) => chapter.nodeStart < nodeCount);
}

/** PUT /books/:id/proposal. */
export const ProposalDraftSchema = z
  .strictObject(draftShape)
  .refine(draftIsValid, { message: DRAFT_MESSAGE });
export type ProposalDraft = z.infer<typeof ProposalDraftSchema>;

/** POST /books/:id/proposal/confirm: the Book facts plus the final draft. */
export const ConfirmRequestSchema = z
  .strictObject({
    ...draftShape,
    title: z.string().trim().min(1).max(300),
    authorName: z.string().trim().max(4000).nullable(),
    language: BookLanguageSchema,
  })
  .refine(draftIsValid, { message: DRAFT_MESSAGE });
export type ConfirmRequest = z.infer<typeof ConfirmRequestSchema>;
